import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StatusBar,
  SafeAreaView,
  Alert,
  Modal,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet } from '@/utils/appStyleSheet';
import { FontFamilies } from '@/constants/Typography';
import { Colors } from '@/constants/Colors';
import { router } from 'expo-router';
import Icons from '@/assets/icons';
import * as StoreReview from 'expo-store-review';
import FeedbackServices from '@/services/feedbackServices';
import useUserStore from '@/store/user';

const RateUsDetailScreen = () => {
  const { isFreePlan } = useUserStore();
  const [rating, setRating] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleRating = (value: number) => {
    setRating(value);
  };

  const goToNextScreen = () => {
    if (isFreePlan) {
      router.replace('/signup/onboard-paywall');
    } else {
      router.replace('/signup/onboard-success');
    }
  };

  const showThankYouAlert = () => {
    Alert.alert(
      'Thank you!',
      'We appreciate your feedback.',
      [
        {
          text: 'OK',
          onPress: goToNextScreen,
        },
      ],
    );
  };

  const showFeedbackForm = () => {
    setShowFeedbackModal(true);
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackModal(false);
    setFeedback('');
  };

  const handleFeedbackSubmit = async () => {
    console.log('[RateUsDetail] Feedback submitted:', feedback);
    setShowFeedbackModal(false);

    // Send feedback to backend
    try {
      await FeedbackServices.submitFeedback({
        rating,
        message: feedback,
      });
    } catch (error) {
      console.error('[RateUsDetail] Failed to submit feedback:', error);
    }

    setFeedback('');
    showThankYouAlert();
  };

  const showFeedbackPrompt = () => {
    Alert.alert(
      "We'd love your feedback",
      'Would you like to tell us how we can improve?',
      [
        {
          text: 'Give Feedback',
          style: 'default',
          isPreferred: true,
          onPress: showFeedbackForm,
        },
        {
          text: 'No, thanks',
          style: 'default',
          onPress: goToNextScreen,
        },
      ],
    );
  };

  const handleContinue = async () => {
    console.log('[RateUsDetail] Rating submitted:', rating);

    if (rating >= 4) {
      const isAvailable = await StoreReview.isAvailableAsync();
      if (isAvailable) {
        await StoreReview.requestReview();
        // Wait 2 seconds for native modal to appear before navigating
        setTimeout(() => {
          goToNextScreen();
        }, 2000);
        return;
      }
      goToNextScreen();
    } else {
      showFeedbackPrompt();
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.container}>
        {/* Header with logo */}
        <View style={styles.header}>
          {/* Sparrow Logo */}
          <View style={styles.logoContainer}>
            <Icons.LogoWithTextBlack width={170} height={38} />
          </View>
        </View>

        {/* Main content - centered vertically */}
        <View style={styles.content}>
          <View style={styles.centeredContent}>
            {/* Title */}
            <Text style={styles.title}>
              How would you rate{'\n'}your experience so{'\n'}far?
            </Text>

            {/* Rating Stars */}
            <View style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map((star) => (
                <TouchableOpacity
                  key={star}
                  onPress={() => handleRating(star)}
                  activeOpacity={0.7}
                  style={styles.starButton}
                >
                  {star <= rating ? (
                    <Ionicons name="star" size={40} color="#FFB536" />
                  ) : (
                    <Icons.StarOutlineThin width={40} height={40} />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>

        {/* Bottom button */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.continueButton,
              rating > 0 ? styles.continueButtonEnabled : styles.continueButtonDisabled,
            ]}
            onPress={handleContinue}
            activeOpacity={0.8}
            disabled={rating === 0}
          >
            <Text
              style={[
                styles.continueButtonText,
                rating > 0 ? styles.continueButtonTextEnabled : styles.continueButtonTextDisabled,
              ]}
            >
              Continue
            </Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
      {/* Feedback Modal */}
      <Modal
        visible={showFeedbackModal}
        transparent
        animationType="slide"
        onRequestClose={handleFeedbackCancel}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.modalContainer}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={handleFeedbackCancel}
          />
          <View style={styles.modalContent}>
            {/* Handle bar */}
            <View style={styles.modalHandle} />

            {/* Title */}
            <Text style={styles.modalTitle}>Tell us how we can improve</Text>

            {/* Text Input */}
            <TextInput
              style={styles.feedbackInput}
              placeholder="Type your feedback here..."
              placeholderTextColor="#999"
              multiline
              value={feedback}
              onChangeText={setFeedback}
              autoFocus
            />

            {/* Buttons */}
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={handleFeedbackCancel}
                activeOpacity={0.8}
              >
                <Text style={styles.modalCancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalSubmitButton}
                onPress={handleFeedbackSubmit}
                activeOpacity={0.8}
              >
                <Text style={styles.modalSubmitButtonText}>Submit</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

export default RateUsDetailScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 10,
  },
  progressBarContainer: {
    width: 342,
    height: 6,
    backgroundColor: '#E5E5E5',
    borderRadius: 100,
    marginBottom: 12,
  },
  progressBar: {
    width: 16,
    height: 6,
    backgroundColor: '#0F9129',
    borderRadius: 100,
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  centeredContent: {
    alignItems: 'center',
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 32,
    lineHeight: 38,
    color: '#161D14',
    textAlign: 'center',
    marginBottom: 24,
  },
  starsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  starButton: {
    padding: 2,
  },
  buttonContainer: {
    paddingHorizontal: 24,
  },
  continueButton: {
    width: '100%',
    height: 58,
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  continueButtonDisabled: {
    backgroundColor: '#E5E5E5',
  },
  continueButtonEnabled: {
    backgroundColor: Colors.primaryButton,
  },
  continueButtonText: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: 17,
    textAlign: 'center',
  },
  continueButtonTextDisabled: {
    color: '#161D14',
    opacity: 0.5,
  },
  continueButtonTextEnabled: {
    color: '#FFFFFF',
  },
  // Modal styles
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingHorizontal: 16,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 36,
    height: 4,
    backgroundColor: '#757575',
    borderRadius: 100,
    alignSelf: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
  modalTitle: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    color: '#000000',
    textAlign: 'center',
    paddingVertical: 8,
    letterSpacing: -0.43,
  },
  feedbackInput: {
    backgroundColor: '#F3F3F3',
    borderRadius: 16,
    padding: 20,
    height: 153,
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 16,
    color: '#000000',
    textAlignVertical: 'top',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  modalCancelButton: {
    flex: 1,
    height: 48,
    backgroundColor: 'rgba(120, 120, 128, 0.16)',
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 17,
    color: '#000000',
    letterSpacing: -0.43,
  },
  modalSubmitButton: {
    flex: 1,
    height: 48,
    backgroundColor: Colors.primaryButton,
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalSubmitButtonText: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 17,
    color: '#FFFFFF',
    letterSpacing: -0.43,
  },
});
